import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, Polygon, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { COMPLIANCE_LABEL, fmtDate } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, AlertTriangle } from "lucide-react";

const COLOR = {
  up_to_date: "#10B981",
  due: "#F59E0B",
  overdue: "#EF4444",
  never_visited: "#71717A",
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) map.setView(points[0], 12);
    else map.fitBounds(points, { padding: [50, 50] });
  }, [points, map]);
  return null;
};

export default function PortfolioMap() {
  const [parcels, setParcels] = useState([]);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.get("/parcels").then(({ data }) => setParcels(data)).catch(() => {});
  }, []);

  const filtered = parcels.filter(
    (p) =>
      (category === "all" || p.asset_category === category) &&
      (status === "all" || p.compliance === status)
  );
  const mapped = filtered.filter((p) => p.latitude != null && p.longitude != null);
  const missing = filtered.length - mapped.length;
  const points = useMemo(() => mapped.map((p) => [p.latitude, p.longitude]), [mapped]);

  // A square approximation of the parcel extent, drawn around the pin.
  const ring = (p) => {
    const acres = Number(p.extent_acres) || 0;
    if (acres <= 0) return null;
    const half = Math.sqrt(acres * 4046.86) / 2;
    const dLat = half / 111320;
    const dLng = half / (111320 * Math.cos((p.latitude * Math.PI) / 180));
    return [
      [p.latitude - dLat, p.longitude - dLng],
      [p.latitude - dLat, p.longitude + dLng],
      [p.latitude + dLat, p.longitude + dLng],
      [p.latitude + dLat, p.longitude - dLng],
    ];
  };

  return (
    <div className="p-5 md:p-10 max-w-[1500px] rise" data-testid="map-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-tiny">Geography</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">
            Portfolio map
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {mapped.length} of {filtered.length} asset(s) plotted, coloured by visit compliance.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-44 h-11" data-testid="map-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {["Agricultural", "Commercial", "Residential"].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 h-11" data-testid="map-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.keys(COMPLIANCE_LABEL).map((k) => (
                <SelectItem key={k} value={k}>{COMPLIANCE_LABEL[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {Object.entries(COLOR).map(([k, c]) => (
          <span key={k} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} /> {COMPLIANCE_LABEL[k]}
          </span>
        ))}
      </div>

      <div className="mt-4 surface overflow-hidden" data-testid="map-container">
        <MapContainer center={[17.385, 78.4867]} zoom={8} scrollWheelZoom
          style={{ height: "min(70vh, 640px)", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          {mapped.map((p) => {
            const poly = ring(p);
            const color = COLOR[p.compliance] || COLOR.never_visited;
            return (
              <div key={p.id}>
                {poly && (
                  <Polygon positions={poly} pathOptions={{ color, weight: 1.5, fillOpacity: 0.12 }} />
                )}
                <CircleMarker center={[p.latitude, p.longitude]} radius={8}
                  pathOptions={{ color: "#ffffff", weight: 2, fillColor: color, fillOpacity: 1 }}>
                  <Popup>
                    <div className="min-w-[190px]" data-testid={`map-popup-${p.id}`}>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {p.asset_category}
                      </p>
                      <p className="mt-1 font-semibold text-sm">{p.name}</p>
                      <p className="text-xs text-zinc-600">
                        Sy. {p.survey_number} · {p.extent_acres || 0} ac
                      </p>
                      <p className="text-xs text-zinc-600 mt-1">
                        {[p.village, p.district].filter(Boolean).join(", ")}
                      </p>
                      <p className="text-xs mt-2" style={{ color }}>
                        {COMPLIANCE_LABEL[p.compliance]} · last visit {fmtDate(p.last_visit_at)}
                      </p>
                      {p.open_case_count > 0 && (
                        <p className="text-xs text-red-600 mt-1">{p.open_case_count} open case(s)</p>
                      )}
                      <Link to={`/parcels/${p.id}`} className="mt-2 inline-block text-xs font-semibold text-primary">
                        Open asset →
                      </Link>
                    </div>
                  </Popup>
                </CircleMarker>
              </div>
            );
          })}
        </MapContainer>
      </div>

      {missing > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="map-missing-coords">
          <AlertTriangle size={17} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">{missing} asset(s) have no coordinates</p>
            <p className="mt-1 text-xs text-amber-800">
              Add latitude and longitude on the asset (Edit asset) to plot them here.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {filtered.filter((p) => p.latitude == null).slice(0, 8).map((p) => (
                <Link key={p.id} to={`/parcels/${p.id}`}
                  className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2 py-1 text-xs hover:border-primary hover:text-primary transition-colors duration-200">
                  <MapPin size={11} /> {p.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
